'use client'
import React, { useEffect, useState } from 'react'
import {
  getPickupAreas,
  getTransportSchedule,
  getWareaHouse,
} from '@/services/coverageAreaService'
import {
  Pickup,
  TransportSchedule,
  Warehouse,
} from '@/app/types/coverageAreaTypes'
import { Skeleton } from '@/components/ui/skeleton'
import CompanyBlocker from '@/app/[lng]/components/BlockerModal/CompanyBlocker'
import { useTranslation } from '@/app/i18n/client'
import Image from 'next/image'
import { productStatsData } from '@/services/companyDetailsService'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RootState } from '@/app/globalRedux/store'
import { useSelector } from 'react-redux'

const TransportCoverageAreas: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation()
  const [transportSchedules, setTransportSchedules] = useState<
    TransportSchedule[]
  >([])
  const { access_count, isBeneficiary,accessPer } = useSelector(
    (state: RootState) => state.user
  )
  const [showContent, setShowContent] = useState<boolean | null>(null)
  const [pickupAreas, setPickupAreas] = useState<Pickup[]>([])
  const [wareHouseAreas, setWareHouseAreas] = useState<Warehouse[]>([])
  const [showAllTransportSchedules, setShowAllTransportSchedules] =
    useState(false)
  const [showAllPickupAreas, setShowAllPickupAreas] = useState(false)
  const [showAllWarehouses, setShowAllWarehouses] = useState(false)

  const [isTransportSchedulesLoading, setIsTransportSchedulesLoading] =
    useState(false)
  const [isPickupAreasLoading, setIsPickupAreasLoading] = useState(false)
  const [isWareHouseAreasLoading, setIsWareHouseAreasLoading] = useState(false)
  const [stats, setStats] = useState({
    countries: '',
    regions: '',
    provinces: '',
    cities: '',
  })

  const fetchData = async () => {
    setIsTransportSchedulesLoading(true)
    try {
      const response = await getTransportSchedule(id)
      if (response?.message === 'Access Exhausted') {
        setShowContent(false)
      } else {
        setShowContent(true)
        if (response && response.transport_schedules) {
          setTransportSchedules(response.transport_schedules)
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    setIsTransportSchedulesLoading(false)
  }

  const fetchStatsData = async () => {
    const statsData: any | { message: string; status: string } =
      await productStatsData(id)
    setStats(statsData)
  }

  const fetchPickupAreas = async () => {
    setIsPickupAreasLoading(true)
    try {
      const response = await getPickupAreas(id)
      if (response && response.pickup_areas) {
        setPickupAreas(response.pickup_areas)
      }
    } catch (error) {
      console.error('Error fetching pickup areas:', error)
    }
    setIsPickupAreasLoading(false)
  }

  const fetchWareHouse = async () => {
    setIsWareHouseAreasLoading(true)
    try {
      const response = await getWareaHouse(id)
      if (response && response.warehouses) {
        setWareHouseAreas(response.warehouses)
      }
    } catch (error) {
      console.error('Error fetching warehouses :', error)
    }
    setIsWareHouseAreasLoading(false)
  }
  useEffect(() => {
    if (transportSchedules?.length <= 0) {
      fetchData()
    }
    if (wareHouseAreas?.length <= 0) {
      fetchWareHouse()
    }
    if (pickupAreas?.length <= 0) {
      fetchPickupAreas()
    }
    fetchStatsData()
  }, [id])

  const visibleTransportSchedules = showAllTransportSchedules
    ? transportSchedules
    : transportSchedules.slice(0, 3)
  const visiblePickupAreas = showAllPickupAreas
    ? pickupAreas
    : pickupAreas.slice(0, 3)
  const visibleWarehouses = showAllWarehouses
    ? wareHouseAreas
    : wareHouseAreas.slice(0, 3)
  if (showContent === null) {
    return <Skeleton className="w-[952px] h-[500px]" />
  }

  return (
    <div className="w-[952px]">
      {!showContent && (
        <CompanyBlocker
          access_count={access_count}
          isBeneficiary={isBeneficiary}
          accessPer={accessPer}
        />
      )}
      <ul className="flex gap-[10px]">
        <li className="bg-orange_card px-[24px] py-[12px] text-[18px] font-ns_bold ">
          <span className="pr-[10px] font-ns_bold text-bright_orange">
            {stats?.countries ? stats?.countries : '-'}
          </span>
          {t('Countries')}
        </li>
        <li className="bg-orange_card px-[24px] py-[12px] text-[18px] font-ns_bold ">
          <span className="pr-[10px] font-ns_bold text-bright_orange">
            {stats?.regions ? stats?.regions : '-'}
          </span>
          {t('Regions')}
        </li>
        <li className="bg-orange_card px-[24px] py-[12px] text-[18px] font-ns_bold ">
          <span className="pr-[10px] font-ns_bold text-bright_orange">
            {stats?.provinces ? stats?.provinces : '-'}
          </span>
          {t('Provincies')}
        </li>
        <li className="bg-orange_card px-[24px] py-[12px] text-[18px] font-ns_bold ">
          <span className="pr-[10px] font-ns_bold text-bright_orange">
            {stats?.cities ? stats?.cities : '-'}
          </span>
          {t('Cities')}
        </li>
      </ul>

      <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.16)] mt-[20px] pb-[26px]">
        <div className="px-[25px] py-[20px] border-b border-[#eaeaea] border-solid text-[16px] text-[#222222] font-ns_bold">
          {t('Scheduled Line')}
        </div>
        <div className="py-[14px] px-[20px]">
          <Table>
            <TableHeader>
              <TableRow className="flex">
                <div className="flex flex-col w-full">
                  <div className="flex">
                    <TableHead className="w-[388px] border-l-0 text-sm h-12 flex justify-center">
                      {t('Departure')}
                    </TableHead>
                    <TableHead className="w-[389px] text-sm h-12 flex justify-center">
                      {t('Arrival')}
                    </TableHead>
                    <TableHead className="w-[133px] text-sm h-12 flex justify-center">
                      {t('Schedule')}
                    </TableHead>
                  </div>
                  <div className="flex">
                    <TableHead className=" border-l-0 text-sm w-[125px]">
                      {t('Country')}
                    </TableHead>
                    <TableHead className=" text-sm w-[152.5px]">
                      {t('Region/Province')}
                    </TableHead>
                    <TableHead className="text-sm w-[110px]">
                      {t('City')}
                    </TableHead>
                    <TableHead className="text-sm w-[125px]">
                      {t('Country')}
                    </TableHead>
                    <TableHead className="text-sm w-[153.5px]">
                      {t('Region/Province')}
                    </TableHead>
                    <TableHead className="text-sm w-[111px]">
                      {t('City')}
                    </TableHead>
                    <TableHead className="text-sm w-[133px]">
                      {t('Day')}
                    </TableHead>
                  </div>
                </div>
              </TableRow>
            </TableHeader>
            <TableBody className="overflow-scroll max-h-[170px]">
              {!isTransportSchedulesLoading &&
              visibleTransportSchedules?.length > 0 ? (
                visibleTransportSchedules?.map((schedule, index) => (
                  <TableRow key={index} className="flex">
                    <div className="flex">
                      <TableCell className="w-[125px]">
                        {schedule?.departure_country?.name ?? t('NA')}
                      </TableCell>
                      <TableCell className="w-[151px]">
                        {schedule?.departure_province?.name ?? t('NA')}
                      </TableCell>
                      <TableCell className="w-[109px]">
                        {schedule?.departure_city?.name ?? t('NA')}
                      </TableCell>
                    </div>
                    <div className="flex">
                      <TableCell className="w-[125px]">
                        {schedule?.arrival_country?.name ?? t('NA')}
                      </TableCell>
                      <TableCell className="w-[151px]">
                        {schedule?.arrival_province?.name ?? t('NA')}
                      </TableCell>
                      <TableCell className="w-[109px]">
                        {schedule?.arrival_city?.name ?? t('NA')}
                      </TableCell>
                    </div>
                    <TableCell className="w-[133px]">
                      {schedule?.day ?? t('NA')}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <div className="flex flex-col gap-2 p-4">
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                </div>
              )}
              {!isTransportSchedulesLoading &&
                transportSchedules?.length === 0 && (
                  <div className="p-16 flex flex-col gap-2 items-center justify-center text-primary-foreground">
                    <Image
                      loading="lazy"
                      width={86}
                      height={94}
                      src="/assets/Icons/noImage/noScheduleIcon.svg"
                      alt=""
                      className="w-[88.5px] h-[58px] object-cover"
                    />
                    <div className="flex">
                      {t('Scheduled Line not available at this moment')}
                    </div>
                  </div>
                )}
            </TableBody>
          </Table>
          {!showAllTransportSchedules && transportSchedules?.length > 3 && (
            <div className="border-b border-l border-r border-solid border-[#eaeaea] flex justify-center py-[11px]">
              <h1
                className="text-[14px] text-primary font-ns_bold cursor-pointer"
                onClick={() => setShowAllTransportSchedules(true)}
              >
                {t('Load More...')}
              </h1>
            </div>
          )}
        </div>
      </div>

      <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.16)] mt-[20px] pb-[26px]">
        <div className="px-[25px] py-[20px] border-b border-[#eaeaea] border-solid text-[16px] text-[#222222] font-ns_bold">
          {t('Pickup Areas')}
        </div>
        <div className="py-[14px] px-[20px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="basis-1/3 w-full border-l-0">
                  {t('Country')}
                </TableHead>
                <TableHead className="basis-1/3 w-full">
                  {t('Region/Province')}
                </TableHead>
                <TableHead className="basis-1/3 w-full">{t('City')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              className={`overflow-scroll ${showAllPickupAreas ? 'max-h-[170px]' : 'max-h-[127px]'}`}
            >
              {!isPickupAreasLoading ? (
                visiblePickupAreas?.map((Pickup, index) => (
                  <TableRow key={index}>
                    <TableCell className="basis-1/3">
                      {Pickup.country?.name || t('NA')}
                    </TableCell>
                    <TableCell className="basis-1/3">
                      {Pickup.province?.name || t('NA')}
                    </TableCell>
                    <TableCell className="basis-1/3">
                      {Pickup.city?.name || t('NA')}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <div className="flex flex-col gap-2 p-4 border-t-0 border border-th_color">
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                </div>
              )}
              {!showAllPickupAreas && pickupAreas?.length > 3 && (
                <div className="border-b border-l border-r border-solid border-[#eaeaea] flex justify-center py-[11px]">
                  <h1
                    className="text-[14px] text-primary font-ns_bold cursor-pointer"
                    onClick={() => setShowAllPickupAreas(true)}
                  >
                    {t('Load More...')}
                  </h1>
                </div>
              )}
              {!isPickupAreasLoading && pickupAreas?.length === 0 && (
                <div className="p-16 flex flex-col gap-2 items-center justify-center text-base text-primary-foreground border-t-0 border border-th_color">
                  {t('Pickup Areas not available at this moment')}
                </div>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.16)] mt-[20px] mb-[70px]">
        <div className="px-[25px] py-[20px] border-b border-[#eaeaea] border-solid text-[16px] text-[#222222] font-ns_bold">
          {t('Warehouses')}
        </div>
        <div className="py-[14px] px-[20px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="basis-1/3 border-l-0">
                  {t('Country')}
                </TableHead>
                <TableHead className="basis-1/3">
                  {t('Region/Province')}
                </TableHead>
                <TableHead className="basis-1/3">{t('City')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              className={`overflow-scroll ${showAllPickupAreas ? 'max-h-[170px]' : 'max-h-[127px]'}`}
            >
              {!isWareHouseAreasLoading ? (
                visibleWarehouses?.map((warehouse, index) => (
                  <TableRow key={index}>
                    <TableCell className="basis-1/3">
                      {warehouse.country?.name || t('NA')}
                    </TableCell>
                    <TableCell className="basis-1/3">
                      {warehouse.province?.name || t('NA')}
                    </TableCell>
                    <TableCell className="basis-1/3">
                      {warehouse.city?.name || t('NA')}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <div className="flex flex-col gap-2 p-4 border-t-0 border border-th_color">
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-full h-4" />
                </div>
              )}
              {!showAllWarehouses && wareHouseAreas.length > 3 && (
                <div className="border-b border-l border-r border-solid border-th_color flex justify-center py-[11px]">
                  <h1
                    className="text-[14px] text-primary font-ns_bold cursor-pointer"
                    onClick={() => setShowAllWarehouses(true)}
                  >
                    {t('Load More...')}
                  </h1>
                </div>
              )}
              {!isWareHouseAreasLoading && wareHouseAreas?.length === 0 && (
                <div className="p-16 flex flex-col gap-2 items-center justify-center text-primary-foreground border-b border-l border-r border-solid border-th_color">
                  {t('Warehouses not available at this moment')}
                </div>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}

export default TransportCoverageAreas
